// ---------------------------------------------------------------------------
// Drapeaux de langue, traces aux proportions officielles.
//
// Les emoji drapeaux etaient exclus : leur dessin change d'un systeme a
// l'autre, Windows n'en rend aucun, et leur chasse ne s'aligne sur rien. Ils
// sont donc dessines ici, une fois, avec les memes cotes partout.
//
// Japon (Nisshoki) : rapport 3:2, disque centre, diametre egal aux trois
// cinquiemes de la hauteur, rouge #BC002D sur blanc.
// France : rapport 3:2, trois bandes egales, bleu #000091 et rouge #E1000F.
// ---------------------------------------------------------------------------

const VUE = '0 0 30 20';

const JP = '<rect width="30" height="20" fill="#fff"/><circle cx="15" cy="10" r="6" fill="#BC002D"/>';
const FR = '<rect width="30" height="20" fill="#fff"/>'
  + '<rect width="10" height="20" fill="#000091"/>'
  + '<rect x="20" width="10" height="20" fill="#E1000F"/>';

// ⚠️ UNE VERSION ORIGINALE N'EST PAS FORCEMENT JAPONAISE.
//
// Toji : « quand c'est de l'anglais, ça met un drapeau américain, pas japonais,
// parce que là le VO est considéré comme japonais ». Le drapeau du Japon etait
// pose sur TOUTE version originale — un heritage du temps ou le service ne
// servait qu'une serie d'animation japonaise. Depuis qu'il porte douze mille
// titres, la VO d'une serie americaine est anglaise, et le Nisshoki sur
// « Lanterns » ne dit pas seulement autre chose : il dit faux.
//
// Les etoiles ne sont pas dessinees : a trente pixels de large, elles font une
// tache grise. Le canton bleu et les bandes suffisent a reconnaitre le drapeau
// a cette taille, et c'est tout ce qu'on lui demande.
const US = '<rect width="30" height="20" fill="#fff"/>'
  + [0, 2, 4, 6, 8, 10, 12].map((y) => `<rect y="${y * 20 / 13}" width="30" height="${20 / 13}" fill="#B22234"/>`).join('')
  + '<rect width="12" height="10.77" fill="#3C3B6E"/>';

// ⚠️ LE DRAPEAU COUPE EN DIAGONALE EST UN DEFAUT, PAS UN DESSIN.
//
// Les deux drapeaux etaient fondus dans un seul carre partage par une
// diagonale : le Japon dans le triangle superieur gauche, la France dans
// l'inferieur droit. L'intention se comprenait — un seul objet pour dire « les
// deux » — mais le resultat ne se lit pas. Ce qu'on voit a la taille reelle
// d'une vignette (30 x 20 px), c'est un rectangle bleu-blanc-rouge barre d'un
// trait, avec un bout de disque rouge coince dans un coin : ni le Nisshoki, ni
// le tricolore, et surtout AUCUN des deux reconnaissable. La question posee
// devant une vignette est « puis-je le regarder en francais ? » ; un drapeau
// hybride que personne n'a jamais vu ailleurs ne repond pas a cette question.
//
// La reponse est donc DEUX drapeaux, cote a cote, entiers : le Japon a gauche,
// la France a droite — l'ordre de lecture d'une version originale puis doublee.
// Voir `drapeauxDe`, qui est desormais le seul chemin correct.
//
// `MIXTE` reste defini pour que `DRAPEAUX.deux` continue d'exister : le dessin
// part de l'interface, pas du fichier, et un import survivant ailleurs ne doit
// pas casser au chargement. Il n'est plus produit par aucune fonction d'ici.
const MIXTE = `<defs>
    <clipPath id="d-haut"><path d="M0 0H30L0 20Z"/></clipPath>
    <clipPath id="d-bas"><path d="M30 0V20H0Z"/></clipPath>
  </defs>
  <g clip-path="url(#d-haut)">${JP}</g>
  <g clip-path="url(#d-bas)">${FR}</g>
  <path d="M30 0 0 20" stroke="rgba(0,0,0,.45)" stroke-width=".9" fill="none"/>`;

const enveloppe = (contenu, titre) =>
  `<svg class="drapeau" viewBox="${VUE}" role="img" aria-label="${titre}">`
  + `<title>${titre}</title>${contenu}`
  + '<rect width="30" height="20" fill="none" stroke="rgba(0,0,0,.35)" stroke-width="1"/></svg>';

/**
 * CE QU'UN FICHIER CONTIENT N'EST PAS CE QU'UNE SOURCE ANNONCE.
 *
 * ⚠️ Toji : « pourquoi ça me note qu'il y a du français alors qu'il n'y a pas
 * de français ? » La liste de langues arrive de deux endroits que plus rien ne
 * distinguait à l'écran : le balayage des NOMS DE FICHIERS (une supposition —
 * « MULTI » et « DUAL AUDIO » mentent souvent, verite-langues.js le documente)
 * et la SONDE, qui a ouvert le conteneur et lu ses pistes.
 *
 * Le dessin ne change pas — un drapeau mi-effacé serait un dessin de plus à
 * apprendre, et la feuille de style ne bouge pas d'une ligne. C'est le TEXTE
 * qui dit d'où vient l'information : au survol, et pour un lecteur d'écran.
 *
 * `null` (le défaut) garde la formule d'avant : elle sert là où la question
 * ne se pose pas — la version qu'une PERSONNE a choisie n'est ni annoncée ni
 * mesurée, c'est un choix.
 */
const AVEU = {
  true: ' — vérifiée dans le fichier',
  false: ' — annoncée par la source, jamais vérifiée',
};
const avec = (drapeau, mesure) => {
  const aveu = AVEU[String(mesure)];
  if (!aveu) return drapeau;
  return drapeau.split('">').length > 1
    ? drapeau
      .replace(/aria-label="([^"]*)"/, (_, t) => `aria-label="${t}${aveu}"`)
      .replace(/<title>([^<]*)<\/title>/, (_, t) => `<title>${t}${aveu}</title>`)
    : drapeau;
};

export const DRAPEAUX = {
  vo: enveloppe(JP, 'Version originale japonaise'),
  voEn: enveloppe(US, 'Version originale anglaise'),
  vf: enveloppe(FR, 'Version française'),
  deux: enveloppe(MIXTE, 'Version originale et française'),
};

/**
 * Ce qu'une liste de langues designe reellement.
 *
 * Les etiquettes viennent de trois endroits qui ne se sont jamais concertes :
 * le balayage des noms de fichiers (`vo`, `vf`), les pistes d'un conteneur
 * (`ja`, `jpn`, `fra`) et le reglage de poste (`vostfr`). Elles disent toutes la
 * meme chose, et c'est ici — a un seul endroit — qu'on les fait coincider.
 */
function langues(liste = []) {
  const l = new Set((Array.isArray(liste) ? liste : []).map((x) => String(x).toLowerCase()));
  const japonais = l.has('ja') || l.has('jpn') || l.has('jp');
  const anglais = l.has('en') || l.has('eng');
  return {
    vo: l.has('vo') || l.has('vostfr') || japonais || anglais,
    vf: l.has('vf') || l.has('fr') || l.has('fra') || l.has('fre'),
    // Ce que la VO est VRAIMENT, quand on le sait. `null` : les etiquettes ne
    // disent que « vo », et c'est l'appelant qui tranchera d'apres le type de
    // l'oeuvre.
    langueVo: japonais ? 'ja' : (anglais ? 'en' : null),
  };
}

/**
 * LES DRAPEAUX D'UNE LISTE DE LANGUES — DEUX SEPARES, PAS UN COUPE EN DEUX.
 *
 * C'est la correction du defaut decrit au-dessus de `MIXTE`. Une œuvre
 * disponible dans les deux versions rend maintenant DEUX drapeaux entiers,
 * dans cet ordre : le Japon a gauche, la France a droite. Chacun est
 * reconnaissable a la taille d'une vignette, et le sens est immediat — « les
 * deux existent » — la ou le carre diagonal demandait qu'on le dechiffre.
 *
 * L'ordre n'est pas decoratif : c'est celui de la version originale puis de la
 * version doublee, le meme que dans la console de seance (`g-langue-vo` avant
 * `g-langue-vf`) et le meme que dans le lecteur. Deux endroits qui rangent les
 * memes drapeaux dans un ordre different se lisent comme deux informations
 * differentes.
 *
 * Rend une chaine HTML, vide quand aucune langue n'est connue : ne rien
 * afficher est une reponse juste, poser un drapeau au hasard n'en est pas une.
 */
export function drapeauxDe(liste = [], { type = null, mesure = null } = {}) {
  const { vo, vf, langueVo } = langues(liste);
  const bouts = [];
  // ⚠️ LE TYPE TRANCHE QUAND LES ETIQUETTES SE TAISENT.
  //
  // Le balayage des noms de fichiers ne rend souvent que « vo » : il sait qu'il
  // y a une version originale, pas dans quelle langue. Le type de l'oeuvre, lui,
  // le dit presque toujours — la VO d'un anime est japonaise, celle d'une serie
  // ou d'un film ne l'est pas. Mieux vaut cette regle qu'un drapeau pose au
  // hasard, et elle cede des qu'une etiquette de langue est presente.
  const enJaponais = langueVo === 'ja' || (langueVo === null && type === 'anime');
  // ⚠️ L'AVEU NE PORTE QUE SUR LA VERSION FRANÇAISE, ET C'EST VOULU. Qu'une
  // œuvre ait une version originale n'est pas une découverte du balayage :
  // c'est une tautologie. La question posée devant une vignette est « puis-je
  // le regarder en français ? » — c'est cette réponse-là qui doit dire si on
  // l'a vérifiée.
  if (vo) bouts.push(enJaponais ? DRAPEAUX.vo : DRAPEAUX.voEn);
  if (vf) bouts.push(avec(DRAPEAUX.vf, mesure));
  // L'espace insecable tient les deux drapeaux ensemble sur la meme ligne : un
  // retour a la ligne entre eux couperait la reponse en deux.
  return bouts.join(' ');
}

/**
 * @deprecated Utiliser `drapeauxDe`, qui rend les deux drapeaux separes.
 *
 * Conserve comme alias — et NON comme l'ancien dessin — pour qu'un appel oublie
 * quelque part rende desormais le bon resultat au lieu du carre diagonal. Un
 * repli qui garde le defaut ne repare rien.
 */
export function drapeauDe(liste = []) {
  return drapeauxDe(liste);
}
